import { motion } from "framer-motion";
import { Leaf, Flame } from "lucide-react";
import { IMAGES, HEART_FLAG_URL } from "@/brand";

const EASE = [0.22, 1, 0.36, 1];

const ROOTS = [
    {
        icon: Leaf,
        title: "Grenada — the Spice Isle",
        accent: "text-thymeglow",
        border: "hover:border-thyme/50",
        text: "Nutmeg, clove and allspice grown in volcanic soil. Grenada gives our blends their warmth — the deep, round sweetness you taste before the heat arrives.",
        chips: ["Nutmeg", "Clove", "Allspice"],
    },
    {
        icon: Flame,
        title: "Jamaica — jerk fire",
        accent: "text-bonnetglow",
        border: "hover:border-bonnet/50",
        text: "Scotch bonnet, thyme and pimento smoke. Jamaica brings the fire — jerk the way it's done over open pimento wood, patient and unapologetic.",
        chips: ["Scotch bonnet", "Thyme", "Smoked pimento"],
    },
];

export const Story = () => (
    <section id="story" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36" data-testid="story-section-content">
        <div className="grid items-start gap-16 lg:grid-cols-[1.1fr_1fr]">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: EASE }}
            >
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-bonnetglow">Our story</p>
                <h2 className="mt-4 font-editorial text-4xl font-medium leading-[1.05] tracking-tight text-parchment sm:text-5xl">
                    Two islands, <span className="italic text-nutmeg">one kitchen table.</span>
                </h2>
                <div className="mt-8 space-y-6 text-base leading-relaxed text-sand sm:text-lg">
                    <p>
                        SpiceJam Kitchen started where all good things do — at a family table stretched between two
                        islands. Grenada on one side, fragrant with nutmeg and clove. Jamaica on the other, loud
                        with scotch bonnet and pimento smoke.
                    </p>
                    <p>
                        Curtis carried both with him to Ontario, and for years the sauces lived only in his kitchen —
                        made in small pots, argued over, perfected, and passed around to anyone lucky enough to be
                        nearby. Now those same recipes are being bottled, batch by small batch, for the rest of the
                        province.
                    </p>
                    <p className="font-editorial text-xl italic text-parchment">
                        Nothing rushed. Nothing watered down. Just island flavour, made properly.
                    </p>
                </div>
                <div className="mt-10 flex items-center gap-4">
                    {HEART_FLAG_URL && (
                        <img
                            src={HEART_FLAG_URL}
                            alt="Grenada x Jamaica heart flag"
                            className="h-14 w-auto"
                            data-testid="story-heart-flag"
                        />
                    )}
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sand/60">
                        — The SpiceJam family, Ontario
                    </p>
                </div>
            </motion.div>

            <div className="space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 48 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE }}
                    className="overflow-hidden rounded-3xl border border-white/10"
                >
                    <img
                        src={IMAGES.spiceBowls}
                        alt="Ground nutmeg, pimento and allspice"
                        className="h-72 w-full object-cover transition-transform duration-700 ease-out hover:scale-105 sm:h-80"
                    />
                </motion.div>
                {ROOTS.map((r, i) => (
                    <motion.div
                        key={r.title}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.1 + i * 0.12 }}
                        className={`rounded-3xl border border-white/10 bg-soot p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 ${r.border}`}
                        data-testid={`story-root-card-${i}`}
                    >
                        <div className="flex items-center gap-3">
                            <r.icon className={`h-5 w-5 ${r.accent}`} />
                            <h3 className="font-display text-lg font-extrabold uppercase tracking-tight text-parchment">
                                {r.title}
                            </h3>
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-sand">{r.text}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {r.chips.map((c) => (
                                <span key={c} className="rounded-full bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-sand/80">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

export default Story;
